/**
 * usePfStepperVariant — which progress-stepper placement the PF revamp shell uses
 * on a wide viewport:
 *
 *   - 'topbar' : stepper rendered inline inside the top bar, which reclaims the
 *                band row beneath it. **The default**, settled in the 2026-09-18
 *                design review.
 *   - 'band'   : stepper on its own row below the top bar — the roomier layout
 *                the A/B started from, kept so the two can still be compared.
 *
 * Resolution order (first match wins):
 *   1. `?pfStepper=topbar|band` URL query param — captured once and persisted to
 *      localStorage so it survives the in-app navigations that drop the query
 *      string. Share a `?pfStepper=band` link to see the other layout live.
 *   2. localStorage `pf-stepper-variant` — the sticky session override (set by a
 *      previous query param, or by hand in devtools).
 *   3. 'topbar'.
 *
 * **The flag is gone deliberately.** `allow-pf-revamp-stepper-in-topbar` never
 * existed in LaunchDarkly, and an unknown flag reads `false` — so the A/B was
 * only ever reachable through the override, and its "default" was band by
 * accident rather than by decision. Now that topbar IS the decision, a flag that
 * can only turn the agreed design off is not a safety net.
 *
 * Mobile is unaffected — `StepShell` keeps the band layout on small viewports
 * whatever this returns, because the slim mobile bar does not fit inline.
 */

import { useEffect, useState } from 'react'

export type PfStepperVariant = 'topbar' | 'band'

const STORAGE_KEY = 'pf-stepper-variant'
const QUERY_KEY = 'pfStepper'

/** Placement on a wide viewport when nobody has asked for the other one. */
const DEFAULT_VARIANT: PfStepperVariant = 'topbar'

const isVariant = (value: string | null): value is PfStepperVariant =>
  value === 'topbar' || value === 'band'

/** Read + validate the `?pfStepper=` query param (null when absent/invalid). */
const variantFromQuery = (): PfStepperVariant | null => {
  if (typeof window === 'undefined') return null
  const raw = new URLSearchParams(window.location.search).get(QUERY_KEY)
  return isVariant(raw) ? raw : null
}

/** Read + validate the persisted localStorage override (null when absent). */
const variantFromStorage = (): PfStepperVariant | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isVariant(raw) ? raw : null
  } catch {
    return null
  }
}

export const usePfStepperVariant = (): PfStepperVariant => {
  // Capture an explicit `?pfStepper=` override into localStorage on mount so it
  // persists across the query-dropping in-app navigations.
  const [override, setOverride] = useState<PfStepperVariant | null>(
    () => variantFromQuery() ?? variantFromStorage()
  )

  useEffect(() => {
    const fromQuery = variantFromQuery()
    if (fromQuery) {
      try {
        window.localStorage.setItem(STORAGE_KEY, fromQuery)
      } catch {
        /* ignore quota / disabled storage — the in-memory override still holds */
      }
      setOverride(fromQuery)
    }
  }, [])

  return override ?? DEFAULT_VARIANT
}
